<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RuleDimension } from "@/modules/rule_engine/ruleEngineClasses";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";

const { t, te } = useI18n();
const store = useRuleEnginedStore();

const props = defineProps<{ dimension?: RuleDimension | null }>();

const emit = defineEmits<{ apply: [RuleDimension] }>();

const isDialogVisible = defineModel<boolean>({ required: true });

const isAvailableHere = (candidate: RuleDimension) =>
	candidate.available_for_rule_sets === null ||
	candidate.available_for_rule_sets.includes(
		store.ruleEngineState.selectedModelVersionType
	);

const availableDimensions = computed(() =>
	store.ruleEngineState.ruleDimensions.filter(isAvailableHere)
);

const dataSources = computed(() => [
	...new Set(availableDimensions.value.map((d) => d.data_source)),
]);

const activeDataSource = ref<string | null>(null);
const chosenFamily = ref<string | null>(null);
const selectedDimensionUuid = ref<string | null>(null);
const selectedAbTestUuid = ref<string | null>(null);

const families = computed(() => [
	...new Set(
		availableDimensions.value
			.filter((d) => d.data_source === activeDataSource.value)
			.map((d) => d.family_name_clean)
	),
]);

/**
 * Falls back to the first family, so switching data source can never leave the
 * list empty. The setter ignores the empty value the tabs emit while the
 * previous source's families are being torn down.
 */
const activeFamily = computed({
	get: () =>
		families.value.includes(chosenFamily.value)
			? chosenFamily.value
			: (families.value[0] ?? null),
	set: (family: string | null) => {
		if (family) chosenFamily.value = family;
	},
});

const displayedDimensions = computed(() =>
	availableDimensions.value.filter(
		(candidate) =>
			candidate.data_source === activeDataSource.value &&
			candidate.family_name_clean === activeFamily.value
	)
);

/** A/B tests are variants of the single `ab_test` dimension, not dimensions. */
const isAbTestSource = computed(() => activeDataSource.value === "ab_test");

const abTests = computed(() => store.ruleEngineState.abTests);

const abTestDimension = computed(() =>
	availableDimensions.value.find(
		(candidate) => candidate.name_clean === "ab_test"
	)
);

const selectedDimension = computed((): RuleDimension | null => {
	if (isAbTestSource.value) {
		if (!abTestDimension.value || !selectedAbTestUuid.value) return null;

		return { ...abTestDimension.value, ab_test_uuid: selectedAbTestUuid.value };
	}

	return (
		availableDimensions.value.find(
			(candidate) => candidate.uuid === selectedDimensionUuid.value
		) ?? null
	);
});

/** Open on whatever is configured, so the dialog picks up where it left off. */
watch(
	isDialogVisible,
	(visible) => {
		if (!visible) return;

		activeDataSource.value =
			props.dimension?.data_source ?? dataSources.value[0] ?? null;
		chosenFamily.value = props.dimension?.family_name_clean ?? null;
		selectedDimensionUuid.value = props.dimension?.uuid ?? null;
		selectedAbTestUuid.value = props.dimension?.ab_test_uuid ?? null;
	},
	{ immediate: true }
);

const translated = (key: string) => (te(key) ? t(key) : "");

const dimensionTitle = (dimension: RuleDimension) =>
	translated(`rule_engine.rule_dimensions.${dimension.name_clean}.title`) ||
	dimension.name_clean;

const dimensionDescription = (dimension: RuleDimension) =>
	translated(`rule_engine.rule_dimensions.${dimension.name_clean}.description`);

const applySelection = () => {
	if (!selectedDimension.value) return;

	emit("apply", selectedDimension.value);
	isDialogVisible.value = false;
};
</script>

<template>
	<v-dialog v-model="isDialogVisible" persistent width="50vw">
		<v-card elevation="9">
			<v-toolbar flat>
				<v-toolbar-title>Select a dimension</v-toolbar-title>
				<v-spacer />
				<v-btn icon variant="text" @click="isDialogVisible = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>

			<v-tabs
				v-model="activeDataSource"
				fixed-tabs
				align-tabs="center"
				bg-color="secondary"
				height="60"
				center-active
				show-arrows
			>
				<v-tab
					v-for="dataSource in dataSources"
					:key="dataSource"
					:value="dataSource"
					:prepend-icon="`fa: fa-light ${t(
						`rule_engine.data_sources.${dataSource}.icon`
					)}`"
				>
					{{ t(`rule_engine.data_sources.${dataSource}.title`) }}
				</v-tab>
			</v-tabs>

			<v-divider />

			<div class="d-flex browse-pane">
				<div class="family-pane pa-2">
					<v-tabs
						v-model="activeFamily"
						direction="vertical"
						bg-color="transparent"
					>
						<v-tab
							v-for="family in families"
							:key="family"
							:value="family"
							:prepend-icon="`fa: fa-light ${t(
								`rule_engine.rule_families.${family}.icon`
							)}`"
						>
							{{ t(`rule_engine.rule_families.${family}.title`) }}
						</v-tab>
					</v-tabs>
				</div>

				<v-divider vertical />

				<div class="pa-4 flex-grow-1 overflow-y-auto">
					<template v-if="isAbTestSource">
						<div v-if="abTests.length === 0" class="dimension-description">
							There are no A/B tests to pick from.
						</div>
						<v-radio-group v-else v-model="selectedAbTestUuid" hide-details>
							<v-radio
								v-for="abTest in abTests"
								:key="abTest.uuid"
								:value="abTest.uuid"
								:label="abTest.name"
								density="comfortable"
							/>
						</v-radio-group>
					</template>

					<v-radio-group v-else v-model="selectedDimensionUuid" hide-details>
						<v-radio
							v-for="dimension in displayedDimensions"
							:key="dimension.uuid"
							:value="dimension.uuid"
							density="comfortable"
						>
							<template #label>
								<div>
									<div>{{ dimensionTitle(dimension) }}</div>
									<div class="dimension-description">
										{{ dimensionDescription(dimension) }}
									</div>
								</div>
							</template>
						</v-radio>
					</v-radio-group>
				</div>
			</div>

			<v-divider />

			<v-card-actions class="pa-3">
				<v-spacer />
				<v-btn variant="text" color="error" @click="isDialogVisible = false">
					Cancel
				</v-btn>
				<v-btn
					class="ml-4"
					variant="tonal"
					color="primary"
					:disabled="!selectedDimension"
					@click="applySelection"
				>
					Apply
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss">
.browse-pane {
	height: 60vh;
}

.family-pane {
	width: 250px;
	flex: none;
	overflow-y: auto;
	background-color: rgb(var(--color-background));
}

.dimension-description {
	font-size: 0.8rem;
	opacity: 0.7;
}
</style>
